import React, { useState, useEffect, useRef } from 'react';
import './UserInfo.css';
import iconBack from '../../assets/icon-back-2.svg';
import iconSearch from '../../assets/images/icon-search.svg';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../contexts/AlertContext';
import { supabase } from '../../lib/supabase';

// Daum 우편번호 서비스 타입 선언
declare global {
  interface Window {
    daum: any;
  }
}

interface UserInfoProps {
  onBack: () => void;
}

type EditMode = null | 'name' | 'phone' | 'carrier' | 'email' | 'address' | 'depositBank' | 'paymentBank' | 'paymentCard';

const UserInfo: React.FC<UserInfoProps> = ({ onBack }) => {
  const { customer, refreshCustomer, deleteAccount } = useAuth();
  const { showConfirm, showAlert } = useAlert();
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [carrier, setCarrier] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');

  // 입금 계좌 정보
  const [depositAccountNumber, setDepositAccountNumber] = useState('');
  const [depositBank, setDepositBank] = useState('');
  const [showBankModal, setShowBankModal] = useState(false);

  // 납부 정보
  const [paymentType, setPaymentType] = useState<'account' | 'card'>('account');
  const [paymentAccountNumber, setPaymentAccountNumber] = useState('');
  const [paymentBank, setPaymentBank] = useState('');
  const [cardCompany, setCardCompany] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiryMonth, setCardExpiryMonth] = useState('');
  const [cardExpiryYear, setCardExpiryYear] = useState('');

  // 주소 검색
  const [showPostcode, setShowPostcode] = useState(false);
  const postcodeRef = useRef<HTMLDivElement>(null);

  // DB에서 고객 정보 불러오기
  useEffect(() => {
    if (customer) {
      setName(customer.name || '');
      setPhone(customer.phone || '');
      setCarrier(customer.carrier || '');
      setEmail(customer.email || '');
      setAddress(customer.address || '');
      setDepositAccountNumber(customer.deposit_account_number || '');
      setDepositBank(customer.deposit_bank || '');
      setPaymentType((customer.payment_type as 'account' | 'card') || 'account');
      setPaymentAccountNumber(customer.payment_account_number || '');
      setPaymentBank(customer.payment_bank || '');
      setCardCompany(customer.card_company || '');
      setCardNumber(customer.card_number || '');
      setCardExpiryMonth(customer.card_expiry_month || '');
      setCardExpiryYear(customer.card_expiry_year || '');
    }
  }, [customer]);

  // Daum 우편번호 서비스 스크립트 로드
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // 주소 검색 모달 열기
  const handleSearchAddress = () => {
    if (!window.daum) {
      alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setShowPostcode(true);

    setTimeout(() => {
      if (postcodeRef.current) {
        new window.daum.Postcode({
          oncomplete: function(data: any) {
            const sido = data.sido;
            const sigungu = data.sigungu;
            const bname = data.bname;
            const shortAddress = `${sido} ${sigungu} ${bname}`.trim();

            setAddress(shortAddress);
            setShowPostcode(false);
          },
          width: '100%',
          height: '100%',
        }).embed(postcodeRef.current);
      }
    }, 100);
  };

  // DB 업데이트 함수
  const updateCustomerInfo = async (field: string, value: any) => {
    if (!customer) return;

    try {
      const { error } = await supabase
        .from('customers')
        .update({ [field]: value })
        .eq('id', customer.id);

      if (error) {
        console.error('업데이트 오류:', error);
        alert('정보 수정 중 오류가 발생했습니다.');
        return;
      }

      await refreshCustomer();
    } catch (error) {
      console.error('업데이트 오류:', error);
      alert('정보 수정 중 오류가 발생했습니다.');
    }
  };

  const banks = [
    'NH농협', '카카오뱅크', 'KB국민', '신한',
    '우리', '하나', 'IBK기업', 'SC제일',
    '씨티', '새마을금고', '저축은행', '수협',
    '신협', '우체국', '산림조합', '토스뱅크',
    'KDB산업', 'SBI저축은행', '케이뱅크', '대구',
    '부산', '광주', '제주', '전북',
    '경남', '중소기업', '한국수출입', '농협중앙회',
    '수협중앙회', '산림조합중앙회'
  ];

  if (editMode === 'name') {
    return (
      <div className="userinfo-container">
        <div className="userinfo-header">
          <div className="userinfo-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={() => setEditMode(null)} />
            <h1 className="userinfo-title">이름 수정</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="userinfo-content">
          <h1 className="userinfo-title-large">
            성함or닉네임을<br />입력해 주세요.
          </h1>
          <div className="userinfo-input-section">
            <input
              type="text"
              className="userinfo-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="홍길동"
            />
          </div>
          <div className="userinfo-spacer" />
          <button
            className={`userinfo-btn-primary ${!name ? 'disabled' : ''}`}
            onClick={async () => {
              await updateCustomerInfo('name', name);
              setEditMode(null);
            }}
            disabled={!name}
          >
            수정 완료
          </button>
        </div>
      </div>
    );
  }

  if (editMode === 'carrier') {
    return (
      <div className="userinfo-container">
        <div className="userinfo-header">
          <div className="userinfo-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={() => setEditMode(null)} />
            <h1 className="userinfo-title">통신사 수정</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="userinfo-content">
          <h1 className="userinfo-title-large">
            본인 휴대폰의 통신사를<br />선택해 주세요.
          </h1>
          <div className="carrier-grid">
            <button
              className={`carrier-btn ${carrier === 'SKT' ? 'active' : ''}`}
              onClick={() => setCarrier('SKT')}
            >
              SKT
            </button>
            <button
              className={`carrier-btn ${carrier === 'KT' ? 'active' : ''}`}
              onClick={() => setCarrier('KT')}
            >
              KT
            </button>
            <button
              className={`carrier-btn ${carrier === 'LG' ? 'active' : ''}`}
              onClick={() => setCarrier('LG')}
            >
              LGU+
            </button>
            <button
              className={`carrier-btn ${carrier === '알뜰폰' ? 'active' : ''}`}
              onClick={() => setCarrier('알뜰폰')}
            >
              알뜰폰
            </button>
          </div>
          <div className="userinfo-spacer" />
          <button
            className={`userinfo-btn-primary ${!carrier ? 'disabled' : ''}`}
            onClick={async () => {
              await updateCustomerInfo('carrier', carrier);
              setEditMode(null);
            }}
            disabled={!carrier}
          >
            수정 완료
          </button>
        </div>
      </div>
    );
  }

  if (editMode === 'email') {
    return (
      <div className="userinfo-container">
        <div className="userinfo-header">
          <div className="userinfo-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={() => setEditMode(null)} />
            <h1 className="userinfo-title">이메일 수정</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="userinfo-content">
          <h1 className="userinfo-title-large">
            이메일을<br />입력해 주세요.
          </h1>
          <div className="userinfo-input-section">
            <input
              type="email"
              className="userinfo-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="BBIBBI@gmail.com"
            />
          </div>
          <div className="userinfo-spacer" />
          <button
            className={`userinfo-btn-primary ${!email ? 'disabled' : ''}`}
            onClick={async () => {
              await updateCustomerInfo('email', email);
              setEditMode(null);
            }}
            disabled={!email}
          >
            수정 완료
          </button>
        </div>
      </div>
    );
  }

  if (editMode === 'phone') {
    const formatPhoneNumber = (value: string) => {
      const numbers = value.replace(/[^\d]/g, '');
      if (numbers.length <= 3) return numbers;
      if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    };

    return (
      <div className="userinfo-container">
        <div className="userinfo-header">
          <div className="userinfo-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={() => setEditMode(null)} />
            <h1 className="userinfo-title">휴대폰번호 수정</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="userinfo-content">
          <h1 className="userinfo-title-large">
            휴대폰 번호를<br />입력해 주세요.
          </h1>
          <div className="userinfo-input-section">
            <input
              type="tel"
              className="userinfo-input"
              value={formatPhoneNumber(phone)}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="010-0000-0000"
              maxLength={13}
            />
          </div>
          <div className="userinfo-spacer" />
          <button
            className={`userinfo-btn-primary ${phone.replace(/[^\d]/g, '').length !== 11 ? 'disabled' : ''}`}
            onClick={async () => {
              await updateCustomerInfo('phone', phone.replace(/[^\d]/g, ''));
              setEditMode(null);
            }}
            disabled={phone.replace(/[^\d]/g, '').length !== 11}
          >
            수정 완료
          </button>
        </div>
      </div>
    );
  }

  if (editMode === 'address') {
    return (
      <div className="userinfo-container">
        <div className="userinfo-header">
          <div className="userinfo-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={() => setEditMode(null)} />
            <h1 className="userinfo-title">주소 수정</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="userinfo-content">
          <h1 className="userinfo-title-large">
            거주중인 지역을<br />알려 주세요.
          </h1>
          <div className="userinfo-input-section">
            <div
              className="location-search"
              onClick={handleSearchAddress}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <img src={iconSearch} alt="검색" style={{ width: '20px', height: '20px', opacity: 0.6 }} />
              <input
                type="text"
                className="userinfo-input"
                placeholder="주소를 검색해 주세요."
                value={address}
                readOnly
                style={{ cursor: 'pointer', flex: 1 }}
              />
            </div>
            {address && (
              <div style={{ marginTop: '12px', color: '#beae79', fontSize: '14px' }}>
                <strong>선택된 지역:</strong> {address}
              </div>
            )}
          </div>
          <div className="userinfo-spacer" />
          <button
            className={`userinfo-btn-primary ${!address ? 'disabled' : ''}`}
            onClick={async () => {
              await updateCustomerInfo('address', address);
              setEditMode(null);
            }}
            disabled={!address}
          >
            수정 완료
          </button>
        </div>

        {/* 주소 검색 모달 */}
        {showPostcode && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              backgroundColor: '#1e1e1e',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ color: '#beae79', margin: 0, fontSize: '18px' }}>주소 검색</h2>
              <button
                onClick={() => setShowPostcode(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 8px',
                }}
              >
                ×
              </button>
            </div>
            <div
              ref={postcodeRef}
              style={{
                flex: 1,
                backgroundColor: 'white',
              }}
            />
          </div>
        )}
      </div>
    );
  }

  if (editMode === 'depositBank') {
    return (
      <div className="userinfo-container">
        <div className="userinfo-header">
          <div className="userinfo-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={() => setEditMode(null)} />
            <h1 className="userinfo-title">입금 계좌</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="userinfo-content">
          <h1 className="userinfo-title-large">
            계좌 번호와 은행을<br />입력해 주세요.
          </h1>
          <div className="userinfo-input-section">
            <input
              type="text"
              className="userinfo-input"
              value={depositAccountNumber}
              onChange={(e) => setDepositAccountNumber(e.target.value)}
              placeholder="계좌 번호를 입력해 주세요"
            />
            <div className="userinfo-bank-selector" onClick={() => setShowBankModal(true)}>
              <span className={depositBank ? '' : 'placeholder'}>
                {depositBank || '은행을 선택해 주세요'}
              </span>
              <img src={iconBack} alt="선택" className="userinfo-arrow" />
            </div>
          </div>
          <div className="userinfo-spacer" />
          <button
            className={`userinfo-btn-primary ${(!depositAccountNumber || !depositBank) ? 'disabled' : ''}`}
            onClick={async () => {
              await updateCustomerInfo('deposit_account_number', depositAccountNumber);
              await updateCustomerInfo('deposit_bank', depositBank);
              setEditMode(null);
            }}
            disabled={!depositAccountNumber || !depositBank}
          >
            완료
          </button>
        </div>

        {/* 은행 선택 모달 */}
        {showBankModal && (
          <div className="bank-modal-overlay" onClick={() => setShowBankModal(false)}>
            <div className="bank-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="bank-modal-header">
                <h2 className="bank-modal-title">은행 선택</h2>
                <button className="bank-modal-close" onClick={() => setShowBankModal(false)}>
                  ✕
                </button>
              </div>
              <div className="bank-grid">
                {banks.map((bank) => (
                  <button
                    key={bank}
                    className={`bank-btn ${depositBank === bank ? 'active' : ''}`}
                    onClick={() => {
                      setDepositBank(bank);
                      setShowBankModal(false);
                    }}
                  >
                    {bank}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (editMode === 'paymentBank') {
    return (
      <div className="userinfo-container">
        <div className="userinfo-header">
          <div className="userinfo-header-wrapper">
            <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={() => setEditMode(null)} />
            <h1 className="userinfo-title">납부 방법</h1>
            <div className="header-icon"></div>
          </div>
        </div>
        <div className="userinfo-content">
          <h1 className="userinfo-title-large">
            납부 방법을<br />선택해 주세요.
          </h1>

          {/* 계좌/카드 토글 */}
          <div className="payment-toggle">
            <button
              className={`payment-toggle-btn ${paymentType === 'account' ? 'active' : ''}`}
              onClick={() => setPaymentType('account')}
            >
              계좌
            </button>
            <button
              className={`payment-toggle-btn ${paymentType === 'card' ? 'active' : ''}`}
              onClick={() => setPaymentType('card')}
            >
              카드
            </button>
          </div>

          {paymentType === 'account' ? (
            <div className="userinfo-input-section">
              <input
                type="text"
                className="userinfo-input"
                value={paymentAccountNumber}
                onChange={(e) => setPaymentAccountNumber(e.target.value)}
                placeholder="계좌 번호를 입력해 주세요"
              />
              <div className="userinfo-bank-selector" onClick={() => setShowBankModal(true)}>
                <span className={paymentBank ? '' : 'placeholder'}>
                  {paymentBank || '은행을 선택해 주세요'}
                </span>
                <img src={iconBack} alt="선택" className="userinfo-arrow" />
              </div>
            </div>
          ) : (
            <div className="userinfo-input-section">
              <div className="userinfo-bank-selector" onClick={() => setShowBankModal(true)}>
                <span className={cardCompany ? '' : 'placeholder'}>
                  {cardCompany || '카드사를 선택해 주세요'}
                </span>
                <img src={iconBack} alt="선택" className="userinfo-arrow" />
              </div>
              <input
                type="text"
                className="userinfo-input"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="카드 번호를 입력해 주세요"
                maxLength={19}
              />
              <div className="card-expiry-group">
                <input
                  type="text"
                  className="userinfo-input"
                  value={cardExpiryMonth}
                  onChange={(e) => setCardExpiryMonth(e.target.value)}
                  placeholder="MM"
                  maxLength={2}
                />
                <span className="expiry-divider">/</span>
                <input
                  type="text"
                  className="userinfo-input"
                  value={cardExpiryYear}
                  onChange={(e) => setCardExpiryYear(e.target.value)}
                  placeholder="YY"
                  maxLength={2}
                />
              </div>
            </div>
          )}

          <div className="userinfo-spacer" />
          <button
            className={`userinfo-btn-primary ${
              paymentType === 'account'
                ? (!paymentAccountNumber || !paymentBank) ? 'disabled' : ''
                : (!cardCompany || !cardNumber || !cardExpiryMonth || !cardExpiryYear) ? 'disabled' : ''
            }`}
            onClick={async () => {
              await updateCustomerInfo('payment_type', paymentType);
              if (paymentType === 'account') {
                await updateCustomerInfo('payment_account_number', paymentAccountNumber);
                await updateCustomerInfo('payment_bank', paymentBank);
              } else {
                await updateCustomerInfo('card_company', cardCompany);
                await updateCustomerInfo('card_number', cardNumber);
                await updateCustomerInfo('card_expiry_month', cardExpiryMonth);
                await updateCustomerInfo('card_expiry_year', cardExpiryYear);
              }
              setEditMode(null);
            }}
            disabled={
              paymentType === 'account'
                ? !paymentAccountNumber || !paymentBank
                : !cardCompany || !cardNumber || !cardExpiryMonth || !cardExpiryYear
            }
          >
            완료
          </button>
        </div>

        {/* 은행/카드사 선택 모달 */}
        {showBankModal && (
          <div className="bank-modal-overlay" onClick={() => setShowBankModal(false)}>
            <div className="bank-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="bank-modal-header">
                <h2 className="bank-modal-title">
                  {paymentType === 'account' ? '은행 선택' : '카드사 선택'}
                </h2>
                <button className="bank-modal-close" onClick={() => setShowBankModal(false)}>
                  ✕
                </button>
              </div>
              <div className="bank-grid">
                {banks.map((bank) => (
                  <button
                    key={bank}
                    className={`bank-btn ${
                      (paymentType === 'account' ? paymentBank : cardCompany) === bank ? 'active' : ''
                    }`}
                    onClick={() => {
                      if (paymentType === 'account') {
                        setPaymentBank(bank);
                      } else {
                        setCardCompany(bank);
                      }
                      setShowBankModal(false);
                    }}
                  >
                    {bank}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="userinfo-container">
      <div className="userinfo-header">
        <div className="userinfo-header-wrapper">
          <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="userinfo-title">회원정보</h1>
          <div className="header-icon"></div>
        </div>
      </div>

      <div className="userinfo-content">
        <h2 className="userinfo-page-title">회원정보</h2>

        {/* 내정보 */}
        <section className="userinfo-section">
          <h2 className="userinfo-section-title">내정보</h2>
          <div className="userinfo-card">
            <div className="userinfo-item clickable" onClick={() => setEditMode('name')}>
              <span className="userinfo-label">이름 or 닉네임</span>
              <div className="userinfo-value-group">
                <span className={`userinfo-value ${!name ? 'placeholder' : ''}`}>
                  {name || '이름을 입력해 주세요'}
                </span>
                <img src={iconBack} alt="수정" className="userinfo-arrow" />
              </div>
            </div>
            <div className="userinfo-item clickable" onClick={() => setEditMode('phone')}>
              <span className="userinfo-label bold">휴대폰번호</span>
              <div className="userinfo-value-group">
                <span className={`userinfo-value ${!phone ? 'placeholder' : ''}`}>
                  {phone ? phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3') : '휴대폰번호를 입력해 주세요'}
                </span>
                <img src={iconBack} alt="수정" className="userinfo-arrow" />
              </div>
            </div>
            <div className="userinfo-item clickable" onClick={() => setEditMode('carrier')}>
              <span className="userinfo-label">통신사</span>
              <div className="userinfo-value-group">
                <span className={`userinfo-value ${!carrier ? 'placeholder' : ''}`}>
                  {carrier || '통신사를 선택해 주세요'}
                </span>
                <img src={iconBack} alt="수정" className="userinfo-arrow" />
              </div>
            </div>
            <div className="userinfo-item clickable" onClick={() => setEditMode('email')}>
              <span className="userinfo-label">이메일</span>
              <div className="userinfo-value-group">
                <span className={`userinfo-value ${!email ? 'placeholder' : ''}`}>
                  {email || '이메일을 입력해 주세요'}
                </span>
                <img src={iconBack} alt="수정" className="userinfo-arrow" />
              </div>
            </div>
            <div className="userinfo-item clickable" onClick={() => setEditMode('address')}>
              <span className="userinfo-label">주소</span>
              <div className="userinfo-value-group">
                <span className={`userinfo-value ${!address ? 'placeholder' : ''}`}>
                  {address || '주소를 입력해 주세요'}
                </span>
                <img src={iconBack} alt="수정" className="userinfo-arrow" />
              </div>
            </div>
          </div>
          <p className="userinfo-notice">*개인정보처리방침에 따라 3년간 정보가 보관됩니다.</p>
        </section>

        {/* 입금 계좌정보 */}
        <section className="userinfo-section">
          <div className="userinfo-section-header clickable" onClick={() => setEditMode('depositBank')}>
            <div>
              <h2 className="userinfo-section-title">입금 계좌정보</h2>
              <p className="userinfo-section-subtitle">입금 받으실 계좌</p>
            </div>
            <img src={iconBack} alt="수정" className="userinfo-arrow" />
          </div>
          <div className="userinfo-card">
            <div className="userinfo-item">
              <span className="userinfo-label">은행</span>
              <span className={`userinfo-value ${!depositBank ? 'placeholder' : ''}`}>
                {depositBank || '은행을 선택해 주세요'}
              </span>
            </div>
            <div className="userinfo-item">
              <span className="userinfo-label bold">계좌 번호</span>
              <span className={`userinfo-value ${!depositAccountNumber ? 'placeholder' : ''}`}>
                {depositAccountNumber || '계좌번호를 설정해 주세요'}
              </span>
            </div>
          </div>
          <p className="userinfo-notice">*서비스이용약관에따라 30일이내 삭제됩니다.</p>
        </section>

        {/* 납부 계좌 및 카드 정보 */}
        <section className="userinfo-section">
          <div className="userinfo-section-header clickable" onClick={() => setEditMode('paymentBank')}>
            <div>
              <h2 className="userinfo-section-title">납부 계좌 및 카드 정보</h2>
              <p className="userinfo-section-subtitle">자동납부할 계좌 및 카드</p>
            </div>
            <img src={iconBack} alt="수정" className="userinfo-arrow" />
          </div>

          {/* 납부 계좌 */}
          {paymentType === 'account' && (
            <div className="userinfo-card">
              <div className="userinfo-item">
                <span className="userinfo-label">은행</span>
                <span className={`userinfo-value ${!paymentBank ? 'placeholder' : ''}`}>
                  {paymentBank || '은행을 선택해 주세요'}
                </span>
              </div>
              <div className="userinfo-item">
                <span className="userinfo-label bold">계좌 번호</span>
                <span className={`userinfo-value ${!paymentAccountNumber ? 'placeholder' : ''}`}>
                  {paymentAccountNumber || '계좌번호를 설정해 주세요'}
                </span>
              </div>
            </div>
          )}

          {/* 납부 카드 */}
          {paymentType === 'card' && (
            <div className="userinfo-card">
              <div className="userinfo-item">
                <span className="userinfo-label">카드사</span>
                <span className={`userinfo-value ${!cardCompany ? 'placeholder' : ''}`}>
                  {cardCompany || '카드사를 선택해 주세요'}
                </span>
              </div>
              <div className="userinfo-item">
                <span className="userinfo-label bold">카드 번호</span>
                <span className={`userinfo-value ${!cardNumber ? 'placeholder' : ''}`}>
                  {cardNumber || '카드번호를 설정해 주세요'}
                </span>
              </div>
              <div className="userinfo-item">
                <div className="userinfo-label-group">
                  <span className="userinfo-label bold">유효기간</span>
                  <span className="userinfo-label-sub">(MM/YY)</span>
                </div>
                <span className={`userinfo-value ${(!cardExpiryMonth || !cardExpiryYear) ? 'placeholder' : ''}`}>
                  {cardExpiryMonth && cardExpiryYear ? `${cardExpiryMonth} / ${cardExpiryYear}` : '유효기간을 입력해 주세요'}
                </span>
              </div>
            </div>
          )}

          {/* 납부 방법이 설정되지 않은 경우 */}
          {!paymentType && (
            <div className="userinfo-card">
              <div className="userinfo-item">
                <span className="userinfo-value placeholder">납부 방법을 설정해 주세요</span>
              </div>
            </div>
          )}
          <p className="userinfo-notice">*서비스이용약관에따라 30일이내 삭제됩니다.</p>
        </section>

        {/* 구분선 */}
        <div className="userinfo-divider" />

        {/* 회원탈퇴 */}
        <div
          className="userinfo-item clickable"
          onClick={async () => {
            const confirmed = await showConfirm(
              '정말 탈퇴하시겠습니까?\n\n탈퇴 시 모든 개인정보가 즉시 삭제되며 복구할 수 없습니다.'
            );
            if (!confirmed) return;

            try {
              await deleteAccount();
              window.location.href = '/login';
            } catch (error) {
              showAlert('회원탈퇴 중 오류가 발생했습니다.');
            }
          }}
        >
          <span className="userinfo-label muted">회원탈퇴</span>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
